/**
 * Account Intent Config Service
 * Per-intent automation toggle for the caller's account (JWT-scoped).
 */

import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse } from '@/types';

export interface AccountIntentConfig {
  _id: string;
  accountId: string;
  intentId: string;
  isAutomationEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

/** A tool on an integration; a null provider means the tool on any integration. */
export interface ToolRef {
  name: string;
  provider: string | null;
}

export interface CheckSources {
  sopIds: string[];
  workflowIds: string[];
  knowledgeIds: string[];
}

/** What every automated reply of an intent must do (backend intentAutomationChecks.service.ts). */
export interface IntentChecks {
  /** Each of these is called at least once. */
  requiredTools: ToolRef[];
  /** None of these is ever called. */
  forbiddenTools: ToolRef[];
  /** Each of these is drawn on; drawing on more is fine. */
  sources: CheckSources;
}

/** One intent's checks, the brand's own or the account's (`buId` null) when the brand has none. */
export interface IntentChecksEntry extends IntentChecks {
  intentId: string;
  buId: number | null;
  updatedAt: string;
}

export const accountIntentConfigService = {
  async getEnabledConfigs(scope?: { bu_id?: string }): Promise<AccountIntentConfig[]> {
    const response = await api.get<ApiResponse<{ configs: AccountIntentConfig[]; count: number }>>(
      API_ENDPOINTS.ACCOUNT_INTENT_CONFIGS.ENABLED,
      { params: scope }
    );
    return response.data.data.configs || [];
  },

  async upsertConfig(
    intentId: string,
    isAutomationEnabled: boolean,
    scope?: { bu_id?: string }
  ): Promise<AccountIntentConfig> {
    const response = await api.patch<ApiResponse<{ config: AccountIntentConfig }>>(
      withBuScope(API_ENDPOINTS.ACCOUNT_INTENT_CONFIGS.BASE, scope ?? {}),
      { intentId, isAutomationEnabled }
    );
    return response.data.data.config;
  },

  /** The checks each intent has for the brand. */
  async getChecks(scope?: { bu_id?: string }): Promise<IntentChecksEntry[]> {
    const response = await api.get<ApiResponse<{ checks: IntentChecksEntry[] }>>(API_ENDPOINTS.ACCOUNT_INTENT_CONFIGS.CHECKS, {
      params: scope
    });
    return response.data.data.checks ?? [];
  },

  /** Sets one intent's checks for the brand; checks that ask nothing remove them. */
  async setChecks(intentId: string, checks: IntentChecks, scope?: { bu_id?: string }): Promise<IntentChecksEntry | null> {
    const response = await api.put<ApiResponse<{ checks: IntentChecksEntry | null }>>(
      withBuScope(API_ENDPOINTS.ACCOUNT_INTENT_CONFIGS.CHECKS, scope ?? {}),
      { intentId, checks }
    );
    return response.data.data.checks;
  }
};
